import { clearAccessToken, getAccessToken, refreshSessionTokens } from "./auth-client";
import { extractMessagePage } from "./chat-message-pagination";
import { getWebApiBase } from "./api-base";
import { calendarDuePayload } from "./timezone";

function apiBaseUrl(): string {
  return getWebApiBase();
}

function assertProductionApiConfigured(): void {
  if (import.meta.env.PROD && !apiBaseUrl().trim()) {
    throw new Error(
      "VITE_PROD_BACKEND_URL was not set when this app was built. Rebuild with your production API URL (e.g. Railway).",
    );
  }
}

function mapNetworkError(err: unknown): Error {
  if (err instanceof Error) {
    const m = err.message;
    if (
      err.name === "TypeError" ||
      m === "Load failed" ||
      m === "Failed to fetch" ||
      m.startsWith("NetworkError") ||
      m.includes("Network request failed")
    ) {
      return new Error(
        "Could not reach the API. Check your connection. If this is the hosted site, the API server must allow your domain (CORS) — deploy the latest backend or set CORS_ALLOWED_ORIGINS on Railway.",
      );
    }
    return err;
  }
  return new Error("Could not reach the API.");
}

type ZodIssueLike = {
  path?: unknown[];
  message?: string;
  expected?: string;
  code?: string;
};

function humanizeZodIssues(issues: ZodIssueLike[]): string | undefined {
  const correctiveObjectIssue = issues.find(
    (issue) =>
      issue.code === "invalid_type" &&
      issue.expected === "string" &&
      Array.isArray(issue.path) &&
      issue.path[0] === "correctiveActions",
  );
  if (correctiveObjectIssue) {
    return "The server needs a quick update before this equipment flow can be saved. Wait a minute, then try Publish again.";
  }
  const messages = issues.map((issue) => issue.message?.trim()).filter(Boolean);
  return messages.length > 0 ? messages.join(" ") : undefined;
}

function parseZodErrorMessage(message: string): string | undefined {
  const trimmed = message.trim();
  if (!trimmed.startsWith("[")) return undefined;
  try {
    const issues = JSON.parse(trimmed) as ZodIssueLike[];
    if (!Array.isArray(issues)) return undefined;
    return humanizeZodIssues(issues);
  } catch {
    return undefined;
  }
}

type ApiErrorBody = {
  error?: string | { message?: string; issues?: ZodIssueLike[]; code?: string; teamId?: string; status?: string };
  message?: string;
};

function parseApiErrorMessage(body: unknown): string | undefined {
  if (Array.isArray(body)) {
    const issues = body as ZodIssueLike[];
    if (issues[0]?.message) return humanizeZodIssues(issues) ?? issues[0].message;
    return undefined;
  }
  if (!body || typeof body !== "object") return undefined;

  const record = body as ApiErrorBody;
  const errField = record.error;
  if (errField && typeof errField === "object") {
    if (Array.isArray(errField.issues) && errField.issues.length > 0) {
      return humanizeZodIssues(errField.issues) ?? errField.issues[0]?.message;
    }
    if (typeof errField.message === "string") {
      return parseZodErrorMessage(errField.message) ?? errField.message;
    }
  }
  if (typeof errField === "string") return errField;
  if (typeof record.message === "string") return record.message;
  return undefined;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly teamId?: string;

  constructor(message: string, status: number, details?: { code?: string; teamId?: string }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = details?.code;
    this.teamId = details?.teamId;
  }
}

export function isWorkspaceReadOnlyError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === "WORKSPACE_READ_ONLY";
}

async function readJson<T>(res: Response): Promise<T | null> {
  if (res.status === 204 || res.status === 205) return null;
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  assertProductionApiConfigured();
  const baseUrl = apiBaseUrl();
  const headers: HeadersInit = { ...(init?.headers as Record<string, string> | undefined) };
  const h = new Headers(headers);
  let token = getAccessToken();
  if (token) h.set("Authorization", `Bearer ${token}`);
  if (init?.body && !h.has("Content-Type")) {
    h.set("Content-Type", "application/json");
  }

  let res: Response;
  try {
    res = await fetch(`${baseUrl}${path}`, { ...init, headers: h });
  } catch (e) {
    throw mapNetworkError(e);
  }

  if (res.status === 401) {
    const recovered = await refreshSessionTokens();
    if (recovered) {
      token = getAccessToken();
      const h2 = new Headers(init?.headers as HeadersInit);
      if (token) h2.set("Authorization", `Bearer ${token}`);
      if (init?.body && !h2.has("Content-Type")) {
        h2.set("Content-Type", "application/json");
      }
      try {
        res = await fetch(`${baseUrl}${path}`, { ...init, headers: h2 });
      } catch (e) {
        throw mapNetworkError(e);
      }
    } else {
      clearAccessToken();
    }
  }

  if (!res.ok) {
    const body = await readJson<unknown>(res);
    const msg = parseApiErrorMessage(body);
    const details =
      body && typeof body === "object" && !Array.isArray(body)
        ? (body as ApiErrorBody).error
        : undefined;
    const structured = details && typeof details === "object" ? details : undefined;
    const error = new ApiError(
      msg ??
        (res.status === 404
          ? "Not found — this may not exist or you may not have access."
          : `Request failed (${res.status})`),
      res.status,
      { code: structured?.code, teamId: structured?.teamId },
    );
    if (error.code === "WORKSPACE_READ_ONLY" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("alenio:workspace-read-only", { detail: { teamId: error.teamId } }));
    }
    throw error;
  }
  const parsed = await readJson<T>(res);
  return (parsed ?? {}) as T;
}

export async function apiGetJson<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: "GET" });
}

export async function apiPostJson<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export async function apiPatchJson<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

export async function apiPutJson<T>(path: string, body: unknown): Promise<T> {
  return apiRequest<T>(path, { method: "PUT", body: JSON.stringify(body) });
}

export async function apiDeleteJson<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: "DELETE" });
}

export type WebMeUser = {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
  createdAt: string;
  timezone?: string | null;
  /** Platform admin — Alenio-wide Admin tab /ops. Absent on older API responses. */
  isAdmin?: boolean;
  canStartWorkspaceTrial?: boolean;
  workspaceTrialStartedAt?: string | null;
  workspaceTrialConsumedAt?: string | null;
  /** Enterprise contract orgs this user belongs to (with all linked workspaces). */
  organizations?: WebEnterpriseOrganization[];
};

export type WebEnterpriseOrganization = {
  id: string;
  name: string;
  slug: string;
  accountType: string;
  role: string;
  workspaceLimit?: number;
  workspaceCount?: number;
  canCreateWorkspaces?: boolean;
  teams: Array<{
    id: string;
    name: string;
    inviteCode: string | null;
    timezone?: string | null;
    hasGoFeatures: boolean;
  }>;
};

export type WebTeamRow = {
  id: string;
  name: string;
  image?: string | null;
  timezone?: string | null;
  createdAt: string;
  role: string;
  inviteCode?: string | null;
  _count: { members: number; tasks: number };
  /** Pro/Operations workspace features (tasks, activity) — from GET /web/api/teams; absent on older clients means unknown. */
  hasTeamFeatures?: boolean;
  /** Alenio Go — Operations plan only. */
  hasGoFeatures?: boolean;
};

export type WorkspaceAccessMode = "full" | "read_only";
export type WorkspaceBannerSeverity = "none" | "info" | "warning" | "critical";

export type WebTeamSubscription = {
  id: string;
  teamId: string;
  plan: string;
  status: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
  updatedAt: string;
  billingProvider?: "stripe" | "mobile_store" | "none";
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  remainingDays: number | null;
  canWrite: boolean;
  accessMode: WorkspaceAccessMode;
  bannerSeverity: WorkspaceBannerSeverity;
  hasTeamFeatures: boolean;
  hasGoFeatures: boolean;
};

export type ChatMessageReaction = {
  id: string;
  emoji: string;
  userId: string;
  user: { id: string; name: string | null };
};

export type TeamChatMessage = {
  id: string;
  content: string | null;
  mediaUrl: string | null;
  mediaType: string | null;
  createdAt: string;
  editedAt?: string | null;
  senderId: string;
  teamId: string;
  reactions?: ChatMessageReaction[];
  sender: { id: string; name: string | null; email: string | null; image: string | null };
};

export type TeamTopic = {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  teamId: string;
  _count?: { messages: number };
};

export type GroupWorkspaceContext = {
  label: string;
  workspaces: Array<{ id: string; name: string }>;
  isCrossWorkspace: boolean;
};

export type GroupMemberCandidate = {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
  workspaces: Array<{ id: string; name: string }>;
  workspaceLabel: string;
};

export type DmConversation = {
  id: string;
  type?: "DIRECT" | "GROUP";
  isGroup: boolean;
  name: string | null;
  teamId?: string | null;
  workspaceId?: string | null;
  participants: Array<{ id: string; name: string | null; email: string | null; image: string | null }>;
  recipient: { id: string; name: string | null; email: string | null; image: string | null } | null;
  workspaceContext?: GroupWorkspaceContext | null;
  lastMessage: {
    id: string;
    content: string | null;
    createdAt: string;
    sender?: { id: string; name: string | null };
  } | null;
  createdAt: string;
  updatedAt: string;
};

export type DirectChatMessage = {
  id: string;
  content: string | null;
  mediaUrl: string | null;
  mediaType: string | null;
  createdAt: string;
  editedAt?: string | null;
  senderId: string;
  conversationId: string;
  reactions?: ChatMessageReaction[];
  sender: { id: string; name: string | null; email: string | null; image: string | null };
};

export function fetchWebMe() {
  return apiGetJson<{ data: WebMeUser | null }>("/web/api/me").then((r) => r.data);
}

export function patchApiProfile(body: { name?: string; image?: string | null; timezone?: string | null }) {
  return apiPatchJson<{ data: { id: string; name: string | null; email: string | null; image: string | null; timezone: string | null } }>(
    "/api/profile",
    body,
  ).then((r) => r.data);
}

export function requestEmailChange(newEmail: string) {
  return apiPostJson<{ data: { ok: true; email: string } }>("/api/profile/email-change/request", {
    newEmail,
  }).then((r) => r.data);
}

export function confirmEmailChange(newEmail: string, otp: string) {
  return apiPostJson<{ data: { id: string; name: string | null; email: string | null; image: string | null; timezone: string | null } }>(
    "/api/profile/email-change/confirm",
    { newEmail, otp },
  ).then((r) => r.data);
}

/** Permanently delete the signed-in user and all associated app data (password required). */
export function deleteApiAccount(password: string) {
  return apiRequest<{ data: { deleted: boolean } }>("/api/user", {
    method: "DELETE",
    body: JSON.stringify({ password }),
  });
}

export type AccountDeletionIssue = {
  code: "multi_member_owner" | "active_web_billing" | "mobile_store_billing";
  message: string;
  teamId: string;
  teamName: string;
  blocking: boolean;
};

export type AccountDeletionReadiness = {
  canDelete: boolean;
  issues: AccountDeletionIssue[];
};

export function fetchAccountDeletionReadiness() {
  return apiGetJson<{ data: AccountDeletionReadiness }>("/api/user/deletion-readiness").then((r) => r.data);
}

export async function fetchWebTeams() {
  const [webRes, mobileRes] = await Promise.all([
    apiGetJson<{ data: WebTeamRow[] }>("/web/api/teams"),
    apiGetJson<{ data: Array<{ id: string; image?: string | null }> }>("/api/teams").catch(() => null),
  ]);
  const mobileImageById = new Map((mobileRes?.data ?? []).map((t) => [t.id, t.image]));
  return webRes.data.map((t) => ({
    ...t,
    image: t.image || mobileImageById.get(t.id) || null,
  }));
}

export type WebTeamJoinRequest = {
  id: string;
  teamId: string;
  userId: string;
  status: string;
  createdAt: string;
  user: { id: string; name: string | null; email: string | null; image: string | null };
};

export function fetchTeamJoinRequests(teamId: string) {
  return apiGetJson<{ data: WebTeamJoinRequest[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/join-requests`,
  ).then((r) => r.data);
}

export function approveTeamJoinRequest(teamId: string, requestId: string) {
  return apiPostJson<{ data: { success: boolean } }>(
    `/api/teams/${encodeURIComponent(teamId)}/join-requests/${encodeURIComponent(requestId)}/approve`,
    {},
  );
}

export function rejectTeamJoinRequest(teamId: string, requestId: string) {
  return apiPostJson<{ data: { success: boolean } }>(
    `/api/teams/${encodeURIComponent(teamId)}/join-requests/${encodeURIComponent(requestId)}/reject`,
    {},
  );
}

export type WebGoLoginRequest = {
  id: string;
  teamId: string;
  deviceId: string;
  deviceLabel: string | null;
  status: string;
  createdAt: string;
};

export function fetchTeamGoLoginRequests(teamId: string) {
  return apiGetJson<{ data: WebGoLoginRequest[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/go-login-requests`,
  ).then((r) => r.data);
}

export function approveTeamGoLoginRequest(teamId: string, requestId: string) {
  return apiPostJson<{ data: { success: boolean; hubToken: string } }>(
    `/api/teams/${encodeURIComponent(teamId)}/go-login-requests/${encodeURIComponent(requestId)}/approve`,
    {},
  );
}

export function rejectTeamGoLoginRequest(teamId: string, requestId: string) {
  return apiPostJson<{ data: { success: boolean } }>(
    `/api/teams/${encodeURIComponent(teamId)}/go-login-requests/${encodeURIComponent(requestId)}/reject`,
    {},
  );
}

export type GoLinkResponse =
  | { status: "pending"; requestId: string; teamName: string }
  | { status: "approved"; requestId?: string; teamName: string; hubToken: string };

export function postGoWorkspaceLink(body: { inviteCode: string; deviceId: string; deviceLabel?: string }) {
  return apiPostJson<{ data: GoLinkResponse }>("/api/public/go/link", body).then((r) => r.data);
}

export function fetchGoLinkStatus(deviceId: string, requestId: string) {
  const q = new URLSearchParams({ deviceId, requestId });
  return apiGetJson<{ data: GoLinkResponse }>(`/api/public/go/status?${q}`).then((r) => r.data);
}

export type GoDeviceRow = {
  id: string;
  deviceId: string;
  deviceLabel: string | null;
  updatedAt: string;
  source?: "approved" | "active";
};

export function fetchTeamGoDevices(teamId: string) {
  return apiGetJson<{ data: GoDeviceRow[] }>(`/api/teams/${encodeURIComponent(teamId)}/go-devices`).then(
    (r) => r.data,
  );
}

export function revokeTeamGoDevice(teamId: string, deviceId: string) {
  return apiDeleteJson<{ data: { success: boolean } }>(
    `/api/teams/${encodeURIComponent(teamId)}/go-devices/${encodeURIComponent(deviceId)}`,
  ).then((r) => r.data);
}

export function postGoDeviceCheckIn(hubToken: string, deviceId: string, deviceLabel?: string) {
  return apiPostJson<{
    data: { success: boolean; approved: boolean; linkStatus?: "approved" | "pending" | "rejected" | "none" };
  }>("/api/public/go/check-in", {
    hubToken,
    deviceId,
    deviceLabel,
  }).then((r) => r.data);
}

export type WorkplaceAlertPayload = {
  title?: string;
  body: string;
  targetType: "device" | "all_devices" | "all_users";
  targetDeviceId?: string;
  playSound?: boolean;
};

export function postWorkplaceAlert(teamId: string, body: WorkplaceAlertPayload) {
  return apiPostJson<{ data: { id: string; createdAt: string } }>(
    `/api/teams/${encodeURIComponent(teamId)}/workplace-alerts`,
    body,
  ).then((r) => r.data);
}

export type GoWorkplaceAlert = {
  id: string;
  title: string;
  body: string;
  playSound: boolean;
  soundUrl: string | null;
  createdAt: string;
};

export function fetchGoWorkplaceAlerts(hubToken: string, deviceId: string) {
  const q = new URLSearchParams({ hubToken, deviceId });
  return apiGetJson<{ data: { alerts: GoWorkplaceAlert[] } }>(`/api/public/go/alerts?${q}`).then((r) =>
    Array.isArray(r.data.alerts) ? r.data.alerts : [],
  );
}

export function ackGoWorkplaceAlert(alertId: string, hubToken: string, deviceId: string) {
  return apiPostJson<{ data: { success: boolean } }>(`/api/public/go/alerts/${encodeURIComponent(alertId)}/ack`, {
    hubToken,
    deviceId,
  });
}

// ── Workspace modules (lifecycle + operating mode) ──────────────────────────

export type ModuleStatus = "inactive" | "active";
export type OperatingMode = "testing" | "live";

export type ModuleTestingAccess = {
  requireTestCode: boolean;
  testAccessCode: string | null;
  testCodeExpiresAt: string | null;
  allowedTestingWorkplaceIds: string[];
  allowedTestingUserIds: string[];
  allowedTestingRoles: string[];
};

export type WorkspaceModule = {
  moduleKey: string;
  moduleName: string;
  description: string;
  icon: string;
  tone: string;
  baseHref: string;
  status: ModuleStatus;
  operatingMode: OperatingMode | null;
  setupProgressPercent: number;
  setupCompletedAt: string | null;
  activatedAt: string | null;
  liveStartedAt: string | null;
  testingStartedAt: string | null;
  testingAccess: ModuleTestingAccess;
  updatedAt: string;
};

export type ModuleValidationResult = {
  passed: boolean;
  checks: { key: string; label: string; passed: boolean }[];
  errors: string[];
};

export type ModuleTestSessionRow = {
  id: string;
  moduleKey: string;
  testerName: string | null;
  workplaceName: string | null;
  startedAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
  completedSteps: number;
  failedSteps: number;
  notes: string | null;
};

const modulesBase = (teamId: string) => `/api/teams/${encodeURIComponent(teamId)}/modules`;

export function fetchWorkspaceModules(teamId: string) {
  return apiGetJson<{ data: { modules: WorkspaceModule[] } }>(modulesBase(teamId)).then((r) =>
    Array.isArray(r.data.modules) ? r.data.modules : [],
  );
}

export function fetchWorkspaceModule(teamId: string, moduleKey: string) {
  return apiGetJson<{ data: { module: WorkspaceModule } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}`,
  ).then((r) => r.data.module);
}

export function setWorkspaceModuleStatus(teamId: string, moduleKey: string, status: ModuleStatus) {
  return apiPatchJson<{ data: { module: WorkspaceModule } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/status`,
    { status },
  ).then((r) => r.data.module);
}

export function setWorkspaceModuleSetupProgress(teamId: string, moduleKey: string, setupProgressPercent: number) {
  return apiPatchJson<{ data: { module: WorkspaceModule } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/setup-progress`,
    { setupProgressPercent },
  ).then((r) => r.data.module);
}

export function updateWorkspaceModuleTestingAccess(
  teamId: string,
  moduleKey: string,
  patch: Partial<{
    requireTestCode: boolean;
    testAccessCode: string | null;
    testCodeExpiresAt: string | null;
    allowedTestingWorkplaceIds: string[];
    allowedTestingUserIds: string[];
    allowedTestingRoles: string[];
  }>,
) {
  return apiPatchJson<{ data: { module: WorkspaceModule } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/testing-access`,
    patch,
  ).then((r) => r.data.module);
}

export function generateWorkspaceModuleTestCode(teamId: string, moduleKey: string) {
  return apiPostJson<{ data: { module: WorkspaceModule } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/generate-test-code`,
    {},
  ).then((r) => r.data.module);
}

export function validateWorkspaceModule(teamId: string, moduleKey: string) {
  return apiPostJson<{ data: { validation: ModuleValidationResult } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/validate`,
    {},
  ).then((r) => r.data.validation);
}

export function goLiveWorkspaceModule(teamId: string, moduleKey: string) {
  return apiPostJson<{ data: { module: WorkspaceModule } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/go-live`,
    {},
  ).then((r) => r.data.module);
}

export function switchWorkspaceModuleToTesting(teamId: string, moduleKey: string) {
  return apiPostJson<{ data: { module: WorkspaceModule } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/switch-to-testing`,
    {},
  ).then((r) => r.data.module);
}

export function fetchWorkspaceModuleTestSessions(teamId: string, moduleKey: string) {
  return apiGetJson<{ data: { sessions: ModuleTestSessionRow[] } }>(
    `${modulesBase(teamId)}/${encodeURIComponent(moduleKey)}/test-sessions`,
  ).then((r) => r.data.sessions);
}

// Public kiosk module surface

export type KioskModule = {
  moduleKey: string;
  moduleName: string;
  description: string;
  icon: string;
  tone: string;
  baseHref: string;
  operatingMode: OperatingMode;
  requireTestCode: boolean;
};

export function fetchGoKioskModules(hubToken: string) {
  const q = new URLSearchParams({ hubToken });
  return apiGetJson<{ data: { modules: KioskModule[] } }>(`/api/public/go/modules?${q}`).then((r) => r.data.modules);
}

export function verifyGoModuleTestCode(hubToken: string, deviceId: string, moduleKey: string, code: string) {
  return apiPostJson<{ data: { success: boolean } }>(
    `/api/public/go/modules/${encodeURIComponent(moduleKey)}/verify-test-code`,
    { hubToken, deviceId, code },
  ).then((r) => r.data);
}

export type WebTeamInvite = {
  id: string;
  teamId: string;
  email: string;
  invitedById: string;
  status: string;
  acceptedUserId: string | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  invitedBy?: { id: string; name: string | null; email: string | null; image: string | null };
  acceptedUser?: { id: string; name: string | null; email: string | null; image: string | null } | null;
};

export type WebTeamInvitePreview = {
  email: string;
  found: boolean;
  alreadyMember: boolean;
  pendingInvite: boolean;
  user?: { id: string; name: string | null; email: string | null; image: string | null };
  workspaces?: Array<{
    id: string;
    name: string;
    image: string | null;
    role: string;
    isCurrentTeam: boolean;
  }>;
};

export function fetchTeamInvites(teamId: string) {
  return apiGetJson<{ data: WebTeamInvite[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/invites`,
  ).then((r) => r.data);
}

export function previewTeamInvite(teamId: string, email: string) {
  return apiPostJson<{ data: WebTeamInvitePreview }>(
    `/api/teams/${encodeURIComponent(teamId)}/invites/preview`,
    { email },
  ).then((r) => r.data);
}

export type WebTeamInviteLinkPreview = {
  teamId: string;
  teamName: string;
  teamImage: string | null;
  inviterName: string | null;
  email: string;
  expiresAt: string;
};

export function fetchTeamInviteByToken(token: string) {
  return apiGetJson<{ data: WebTeamInviteLinkPreview }>(
    `/api/team-invites/${encodeURIComponent(token)}`,
  ).then((r) => r.data);
}

export function redeemTeamInvite(token: string) {
  return apiPostJson<{ data: { teamId: string; teamName: string } }>(
    "/api/team-invites/redeem",
    { token },
  ).then((r) => r.data);
}

export type EnterpriseInvitePreview = {
  email: string;
  suggestedName: string | null;
  customerName: string;
  customerSlug: string;
  workspaceName: string | null;
  expiresAt: string;
  token: string;
};

export function fetchEnterpriseInviteByToken(token: string) {
  return apiGetJson<{ data: EnterpriseInvitePreview }>(
    `/api/enterprise-invites/${encodeURIComponent(token)}`,
  ).then((r) => r.data);
}

export function redeemEnterpriseInvite(token: string) {
  return apiPostJson<{
    data: { organizationId: string; organizationName: string; teamId: string | null };
  }>("/api/enterprise-invites/redeem", { token }).then((r) => r.data);
}

export function createEnterpriseOrganizationWorkspace(
  organizationId: string,
  body: { name: string; plan?: "free" | "team" | "pro" | "operations" },
) {
  return apiPostJson<{
    data: {
      team: { id: string; name: string; inviteCode: string; hasGoFeatures: boolean };
      workspaceLimit: number;
      workspaceCount: number;
    };
  }>(`/api/organizations/${encodeURIComponent(organizationId)}/workspaces`, body).then((r) => r.data);
}

export function renameEnterpriseOrganizationWorkspace(
  organizationId: string,
  teamId: string,
  body: { name: string },
) {
  return apiPatchJson<{
    data: { team: { id: string; name: string; inviteCode: string } };
  }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/workspaces/${encodeURIComponent(teamId)}`,
    body,
  ).then((r) => r.data);
}

export function deleteEnterpriseOrganizationWorkspace(organizationId: string, teamId: string) {
  return apiRequest<{
    data: { deletedTeamId: string; deletedName: string };
  }>(`/api/organizations/${encodeURIComponent(organizationId)}/workspaces/${encodeURIComponent(teamId)}`, {
    method: "DELETE",
    body: JSON.stringify({ confirmPhrase: "DELETE" }),
  }).then((r) => r.data);
}

export type OrgGoModulePermissions = {
  allowScheduleEdits: boolean;
  allowEquipmentAdditions: boolean;
  allowLocalNotes: boolean;
  allowLocalNotifications: boolean;
  allowTemplateEdits: boolean;
};

export type OrgGoModule = {
  id: string;
  organizationId: string;
  moduleKey: string;
  moduleName: string;
  status: string;
  defaults: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  assignment: {
    id: string;
    scope: "organization" | "workspaces";
    teamIds: string[];
    teams: Array<{ id: string; name: string }>;
    permissions: OrgGoModulePermissions;
  } | null;
  workspacePermissions?: OrgGoModulePermissions;
};

export function fetchOrgGoOverview(organizationId: string) {
  return apiGetJson<{
    data: {
      workspaceCount: number;
      libraryItemCount: number;
      moduleCount: number;
      publishedModuleCount: number;
      workspacesWithAssignments: number;
      modules: OrgGoModule[];
    };
  }>(`/api/organizations/${encodeURIComponent(organizationId)}/go/overview`).then((r) => r.data);
}

export type OrgDirectoryMember = {
  id: string;
  userId: string;
  name: string | null;
  email: string;
  image: string | null;
  role: string;
  scope: "organization" | "workspace";
  workspaceId: string | null;
  workspaceName: string | null;
};

export function fetchOrgMembers(organizationId: string) {
  return apiGetJson<{ data: { members: OrgDirectoryMember[] } }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/members`,
  ).then((r) => r.data.members);
}

export function fetchOrgGoModules(organizationId: string) {
  return apiGetJson<{ data: { modules: OrgGoModule[] } }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/go/modules`,
  ).then((r) => r.data.modules);
}

export function upsertOrgGoModule(
  organizationId: string,
  body: {
    moduleKey: string;
    moduleName?: string;
    status?: "draft" | "published" | "archived";
    defaults?: Record<string, unknown>;
  },
) {
  return apiPostJson<{ data: OrgGoModule }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/go/modules`,
    body,
  ).then((r) => r.data);
}

export function setOrgGoModuleAssignment(
  organizationId: string,
  moduleId: string,
  body: {
    scope: "organization" | "workspaces";
    teamIds?: string[];
    permissions?: Partial<OrgGoModulePermissions>;
  },
) {
  return apiPutJson<{ data: OrgGoModule }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/go/modules/${encodeURIComponent(moduleId)}/assignment`,
    body,
  ).then((r) => r.data);
}

export function fetchOrgGoLibrary(organizationId: string, params?: { q?: string; status?: string }) {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.status) qs.set("status", params.status);
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiGetJson<{ data: { items: unknown[] } }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/go/library${suffix}`,
  ).then((r) => r.data.items);
}

export function createOrgGoLibraryItem(
  organizationId: string,
  body: {
    name: string;
    description?: string | null;
    category?: string;
    type: string;
    instructions?: string | null;
    requiredDefault?: boolean;
    config?: unknown;
    deviceMethods?: unknown;
  },
) {
  return apiPostJson<{ data: { item: unknown } }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/go/library`,
    body,
  ).then((r) => r.data.item);
}

export function fetchWorkspaceAssignedModules(teamId: string) {
  return apiGetJson<{
    data: { enterprise: boolean; modules: OrgGoModule[] };
  }>(`/api/teams/${encodeURIComponent(teamId)}/go/assigned-modules`).then((r) => r.data);
}

export function inviteMemberByEmail(teamId: string, email: string) {
  return apiPostJson<{
    data: {
      added: boolean;
      user?: { id: string; name: string | null; email: string | null; image: string | null };
      role?: string;
      invite?: WebTeamInvite;
      emailSent?: boolean;
    };
  }>(`/api/teams/${encodeURIComponent(teamId)}/invites`, { email }).then((r) => r.data);
}

export function cancelTeamInvite(teamId: string, inviteId: string) {
  return apiDeleteJson<{ data: { cancelled: boolean } }>(
    `/api/teams/${encodeURIComponent(teamId)}/invites/${encodeURIComponent(inviteId)}`,
  ).then((r) => r.data);
}

export function resendTeamInvite(teamId: string, inviteId: string) {
  return apiPostJson<{ data: WebTeamInvite }>(
    `/api/teams/${encodeURIComponent(teamId)}/invites/${encodeURIComponent(inviteId)}/resend`,
    {},
  ).then((r) => r.data);
}

export async function fetchWebTeamSubscription(teamId: string): Promise<WebTeamSubscription> {
  const q = encodeURIComponent(teamId);
  try {
    return await apiGetJson<{ data: WebTeamSubscription }>(`/web/api/teams/${q}/subscription`).then((r) => r.data);
  } catch (e) {
    const msg = e instanceof Error ? e.message : "";
    if (msg.includes("Not found")) {
      return apiGetJson<{ data: WebTeamSubscription }>(`/api/teams/${q}/subscription`).then((r) => r.data);
    }
    throw e;
  }
}

export function postWebBillingCheckout(teamId: string, plan: "pro" | "operations" = "pro") {
  return apiPostJson<{
    data: { url?: string; upgraded?: boolean; subscription?: WebTeamSubscription };
  }>("/web/api/billing/checkout-session", { teamId, plan }).then((r) => r.data);
}

export function postWebBillingPortal(teamId: string) {
  return apiPostJson<{ data: { url: string } }>("/web/api/billing/portal-session", { teamId }).then((r) => r.data);
}

export type WebBillingReconcileResult = {
  subscription: WebTeamSubscription & { billingProvider: "stripe" | "mobile_store" | "none" };
  reconcile: { applied: boolean; message: string };
};

export function postWebBillingReconcile(teamId: string) {
  return apiPostJson<{ data: WebBillingReconcileResult }>("/web/api/billing/reconcile-subscription", { teamId }).then(
    (r) => r.data,
  );
}

export function fetchWebCheckoutConfig() {
  return apiGetJson<{
    data: {
      configured: boolean;
      missingKeys: string[];
      plans?: { pro: boolean; operations: boolean };
    };
  }>("/web/api/billing/checkout-config").then((r) => r.data);
}

export function fetchTeamMessages(teamId: string, topicId: string) {
  const q = new URLSearchParams({ topicId, limit: "50" });
  return apiGetJson<{ data: { messages: TeamChatMessage[]; hasMore: boolean; nextCursor: string | null } }>(
    `/api/teams/${encodeURIComponent(teamId)}/messages?${q.toString()}`,
  ).then((r) => extractMessagePage<TeamChatMessage>(r.data).messages);
}

export function postTeamMessage(
  teamId: string,
  content: string,
  topicId: string,
  media?: { mediaUrl: string; mediaType: string },
) {
  return apiPostJson<{ data: TeamChatMessage }>(`/api/teams/${encodeURIComponent(teamId)}/messages`, {
    content: content.trim() || null,
    topicId: topicId === "general" ? null : topicId,
    ...(media ? { mediaUrl: media.mediaUrl, mediaType: media.mediaType } : {}),
  }).then((r) => r.data);
}

export function toggleTeamMessageReaction(teamId: string, messageId: string, emoji: string) {
  return apiPostJson<{ data: TeamChatMessage }>(
    `/api/teams/${encodeURIComponent(teamId)}/messages/${encodeURIComponent(messageId)}/reactions`,
    { emoji },
  ).then((r) => r.data);
}

export function patchTeamMessage(teamId: string, messageId: string, content: string) {
  return apiPatchJson<{ data: TeamChatMessage }>(
    `/api/teams/${encodeURIComponent(teamId)}/messages/${encodeURIComponent(messageId)}`,
    { content: content.trim() },
  ).then((r) => r.data);
}

export function deleteTeamMessage(teamId: string, messageId: string) {
  return apiDeleteJson<void>(`/api/teams/${encodeURIComponent(teamId)}/messages/${encodeURIComponent(messageId)}`);
}

export function fetchTeamTopics(teamId: string) {
  return apiGetJson<{ data: TeamTopic[] }>(`/api/teams/${encodeURIComponent(teamId)}/topics`).then((r) => r.data);
}

export function createTeamTopic(
  teamId: string,
  input: { name: string; description?: string; color?: string },
) {
  return apiPostJson<{ data: TeamTopic }>(`/api/teams/${encodeURIComponent(teamId)}/topics`, {
    name: input.name.trim(),
    description: input.description?.trim() || undefined,
    color: input.color || "#4361EE",
  }).then((r) => r.data);
}

export function deleteTeamTopic(teamId: string, topicId: string) {
  return apiDeleteJson<void>(
    `/api/teams/${encodeURIComponent(teamId)}/topics/${encodeURIComponent(topicId)}`,
  );
}

export type UserSearchRow = {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
};

export function searchUsers(query: string) {
  const q = query.trim();
  if (!q) return Promise.resolve([] as UserSearchRow[]);
  return apiGetJson<{ data: UserSearchRow[] }>(`/api/users/search?q=${encodeURIComponent(q)}`).then((r) => r.data);
}

export function findOrCreateDm(recipientId: string) {
  return apiPostJson<{ data: DmConversation }>("/api/dms/find-or-create", { recipientId }).then((r) => r.data);
}

export function createGroupDm(input: { name: string; participantIds: string[]; teamId?: string }) {
  return apiPostJson<{ data: DmConversation }>("/api/dms/create-group", {
    name: input.name.trim(),
    participantIds: input.participantIds,
    ...(input.teamId ? { teamId: input.teamId } : {}),
  }).then((r) => r.data);
}

export function fetchGroupMemberCandidates(query = "", teamId?: string) {
  const params = new URLSearchParams();
  if (query.trim().length >= 2) params.set("q", query.trim());
  if (teamId?.trim()) params.set("teamId", teamId.trim());
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return apiGetJson<{ data: GroupMemberCandidate[] }>(`/api/dms/group-member-candidates${suffix}`).then((r) => r.data);
}

export function fetchDmConversations() {
  return apiGetJson<{ data: DmConversation[] }>("/api/dms").then((r) => r.data);
}

export function fetchDmMessages(conversationId: string) {
  return apiGetJson<{ data: { messages: DirectChatMessage[]; hasMore: boolean; nextCursor: string | null } }>(
    `/api/dms/${encodeURIComponent(conversationId)}/messages?limit=50`,
  ).then((r) => extractMessagePage<DirectChatMessage>(r.data).messages);
}

export function postDmMessage(
  conversationId: string,
  content: string,
  media?: { mediaUrl: string; mediaType: string },
) {
  return apiPostJson<{ data: DirectChatMessage }>(`/api/dms/${encodeURIComponent(conversationId)}/messages`, {
    content: content.trim() || null,
    ...(media ? { mediaUrl: media.mediaUrl, mediaType: media.mediaType } : {}),
  }).then((r) => r.data);
}

export function toggleDmMessageReaction(conversationId: string, messageId: string, emoji: string) {
  return apiPostJson<{ data: DirectChatMessage }>(
    `/api/dms/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/reactions`,
    { emoji },
  ).then((r) => r.data);
}

export function deleteDmMessage(conversationId: string, messageId: string) {
  return apiDeleteJson<void>(`/api/dms/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}`);
}

export function deleteDmConversation(conversationId: string) {
  return apiDeleteJson<void>(`/api/dms/${encodeURIComponent(conversationId)}`);
}

export function leaveDmConversation(conversationId: string) {
  return apiPostJson<void>(`/api/dms/${encodeURIComponent(conversationId)}/leave`, {});
}

export type ChatUploadResult = {
  id: string;
  url: string;
  originalFilename: string;
  contentType: string;
  sizeBytes: number;
};

/** Same as mobile `POST /api/upload` — Firebase Storage when configured. */
export async function uploadChatMedia(file: File): Promise<ChatUploadResult> {
  return uploadMediaFile(file);
}

/** Upload a custom workplace alert sound for a workspace. */
export async function uploadGoAlertSound(teamId: string, file: File): Promise<ChatUploadResult> {
  assertProductionApiConfigured();
  const baseUrl = apiBaseUrl();
  async function doUpload(token: string | null) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", "go_alert_sound");
    formData.append("teamId", teamId);
    const h = new Headers();
    if (token) h.set("Authorization", `Bearer ${token}`);
    try {
      return await fetch(`${baseUrl}/api/upload`, { method: "POST", body: formData, headers: h });
    } catch (e) {
      throw mapNetworkError(e);
    }
  }

  let token = getAccessToken();
  let res = await doUpload(token);

  if (res.status === 401) {
    const recovered = await refreshSessionTokens();
    if (recovered) {
      token = getAccessToken();
      res = await doUpload(token);
    } else {
      clearAccessToken();
    }
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? "Upload failed");
  }

  const json = (await res.json()) as { data: ChatUploadResult };
  return json.data;
}

async function uploadMediaFile(file: File): Promise<ChatUploadResult> {
  assertProductionApiConfigured();
  const baseUrl = apiBaseUrl();
  async function doUpload(token: string | null) {
    const formData = new FormData();
    formData.append("file", file);
    const h = new Headers();
    if (token) h.set("Authorization", `Bearer ${token}`);
    try {
      return await fetch(`${baseUrl}/api/upload`, { method: "POST", body: formData, headers: h });
    } catch (e) {
      throw mapNetworkError(e);
    }
  }

  let token = getAccessToken();
  let res = await doUpload(token);

  if (res.status === 401) {
    const recovered = await refreshSessionTokens();
    if (recovered) {
      token = getAccessToken();
      res = await doUpload(token);
    } else {
      clearAccessToken();
    }
  }

  const parsed = (await res.json().catch(() => ({}))) as {
    data?: ChatUploadResult;
    error?: string | { message?: string };
    message?: string;
  };

  if (!res.ok) {
    const msg =
      typeof parsed?.error === "string"
        ? parsed.error
        : typeof parsed?.error === "object" && parsed?.error?.message
          ? parsed.error.message
          : typeof parsed?.message === "string"
            ? parsed.message
            : res.status === 503
              ? "File storage is not configured on the server."
              : `Upload failed (${res.status})`;
    throw new Error(msg);
  }

  if (!parsed.data) throw new Error("Upload response missing data.");
  return parsed.data;
}

export type ApiTask = {
  id: string;
  title: string;
  description: string | null;
  dueDate: string | null;
  priority: string;
  status: string;
  completedAt: string | null;
  archivedAt?: string | null;
  kind?: "workspace_task" | "reminder";
  momentumEligible?: boolean;
  teamId?: string;
  creatorId?: string;
  attachmentUrl?: string | null;
  incognito?: boolean;
  isJoint?: boolean;
  createdAt?: string;
  updatedAt?: string;
  recurrenceSeriesId?: string | null;
  recurrenceRule?: {
    id: string;
    type: string;
    interval: number;
    daysOfWeek?: string | null;
    dayOfMonth?: number | null;
    nextDueAt?: string | null;
  } | null;
  creator?: { id: string; name: string | null; email?: string | null; image?: string | null };
  subtasks?: ApiSubtask[];
  assignments: Array<{
    user: { id: string; name: string | null; email?: string | null; image: string | null };
  }>;
  oneOnOneMeetingId?: string | null;
  oneOnOneMeeting?: {
    id: string;
    memberUserId: string;
    createdById: string;
    templateTitle: string;
    status?: string;
    publishedAt?: string | null;
    createdAt: string;
    createdBy?: { id: string; name: string | null; email?: string | null } | null;
  } | null;
};

export type ApiSubtask = {
  id: string;
  title: string;
  completed: boolean;
  order: number;
  completions?: Array<{
    userId: string;
    user?: { id: string; name: string | null; image?: string | null };
  }>;
};

export type TaskTemplate = {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  attachmentUrl: string | null;
  subtasks: Array<{ title: string; order: number }>;
  isRecurring: boolean;
  recurrenceType: string | null;
  recurrenceInterval: number | null;
  recurrenceDaysOfWeek: string | null;
  recurrenceDayOfMonth: number | null;
  incognito: boolean;
  isJoint: boolean;
};

export type ApiTaskDetail = ApiTask & {
  teamId: string;
  creatorId: string;
  attachmentUrl: string | null;
  incognito: boolean;
  isJoint: boolean;
  createdAt: string;
  updatedAt: string;
  team: { id: string; name: string };
  creator: { id: string; name: string | null; email: string | null; image: string | null };
  subtasks: ApiSubtask[];
};

export function fetchWebTaskDetail(taskId: string, teamId?: string | null) {
  const tid = encodeURIComponent(taskId);
  const ws = teamId?.trim();
  if (!ws) {
    return apiGetJson<{ data: ApiTaskDetail }>(`/web/api/tasks/${tid}`).then((r) => r.data);
  }
  return apiGetJson<{ data: ApiTaskDetail }>(`/web/api/teams/${encodeURIComponent(ws)}/tasks/${tid}`)
    .then((r) => r.data)
    .catch(() => apiGetJson<{ data: ApiTaskDetail }>(`/web/api/tasks/${tid}`).then((r) => r.data));
}

export type ApiCalendarEvent = {
  id: string;
  title: string;
  description?: string | null;
  startDate: string;
  endDate: string | null;
  allDay: boolean | null;
  color?: string | null;
  isHidden?: boolean | null;
  isVideoMeeting?: boolean | null;
  isOneOnOne?: boolean | null;
  assigneeIds?: string[] | null;
  approvalStatus?: "pending" | "approved" | "rejected" | null;
  createdById?: string;
  createdBy?: { id: string; name: string; image?: string | null };
};

export type ActivityMetadata = {
  taskTitle?: string;
  taskTitles?: string[];
  taskCount?: number;
  eventTitle?: string;
  eventTitles?: string[];
  eventCount?: number;
  startDate?: string;
  allDay?: boolean;
  userName?: string;
  count?: number;
  incognito?: boolean;
  assigneeName?: string;
  isVideoMeeting?: boolean;
  targetUserId?: string;
  targetName?: string;
  targetUserImage?: string | null;
  celebrationType?: string;
  message?: string | null;
  assignees?: { id: string; name: string; image: string | null }[];
} | null;

export type ActivityReactionBucket = {
  count: number;
  userIds: string[];
  users: { id: string; name: string | null }[];
};

export type ApiActivityItem = {
  id: string;
  teamId?: string;
  team?: { id: string; name: string } | null;
  type: string;
  createdAt: string;
  metadata: ActivityMetadata;
  user: { id: string; name: string | null; image: string | null } | null;
  reactions: Record<string, ActivityReactionBucket>;
};

export type UpcomingVideoMeeting = {
  event: ApiCalendarEvent & { teamId: string };
  teamName: string;
  userRole: string;
};

/** Web dashboard routes (`/web/...`) — no mobile subscription gate on tasks. */
export function fetchWebTeamTasks(teamId: string) {
  return apiGetJson<{ data: ApiTask[] }>(`/web/api/teams/${encodeURIComponent(teamId)}/tasks`).then((r) => r.data);
}

// Core app route fallback (often deployed earlier than /web routes)
export function fetchCoreTeamTasks(teamId: string) {
  return apiGetJson<{ data: ApiTask[] | { tasks?: ApiTask[] } }>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks?activeOnly=true&limit=200`,
  ).then(
    (r) => {
      const data = r.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.tasks)) return data.tasks;
      return [];
    },
  );
}

export function updateCoreTeamTask(
  teamId: string,
  taskId: string,
  patch: Partial<{
    title: string;
    description: string | null;
    priority: string;
    dueDate: string | null;
    status: string;
    attachmentUrl: string | null;
    scope: "task" | "series";
    timeZone?: string;
  }>,
) {
  return apiPatchJson<{ data: ApiTask }>(`/api/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(taskId)}`, patch).then(
    (r) => r.data,
  );
}

export function fetchWebTeamEvents(teamId: string) {
  return apiGetJson<{ data: ApiCalendarEvent[] }>(
    `/web/api/teams/${encodeURIComponent(teamId)}/events`,
  ).then((r) => r.data);
}

export function fetchPendingCalendarEvents(teamId: string) {
  return apiGetJson<{ data: ApiCalendarEvent[] }>(
    `/web/api/teams/${encodeURIComponent(teamId)}/events/pending`,
  ).then((r) => r.data);
}

export function createWebTeamEvent(
  teamId: string,
  input: {
    title: string;
    description?: string | null;
    startDate: string;
    endDate?: string | null;
    allDay?: boolean;
    color?: string;
    isVideoMeeting?: boolean;
    isHidden?: boolean;
    assigneeIds?: string[];
    reminderMinutes?: number[];
  },
) {
  return apiPostJson<{ data: ApiCalendarEvent }>(`/web/api/teams/${encodeURIComponent(teamId)}/events`, {
    title: input.title,
    description: input.description ?? null,
    startDate: input.startDate,
    endDate: input.endDate ?? null,
    allDay: input.allDay ?? true,
    color: input.color ?? "#4361EE",
    isVideoMeeting: input.isVideoMeeting ?? false,
    isHidden: input.isHidden ?? false,
    assigneeIds: input.assigneeIds,
    reminderMinutes: input.reminderMinutes,
  }).then((r) => r.data);
}

export function deleteWebTeamEvent(teamId: string, eventId: string) {
  return apiRequest<{ data: { ok: true } }>(
    `/web/api/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}`,
    { method: "DELETE" },
  ).then((r) => r.data);
}

export async function deleteWebTask(taskId: string, teamId?: string, scope?: "task" | "series") {
  const tid = encodeURIComponent(taskId);
  const qs = scope === "series" ? "?scope=series" : "";
  try {
    return await apiRequest<{ data: { ok: true } }>(`/web/api/tasks/${tid}${qs}`, { method: "DELETE" }).then((r) => r.data);
  } catch (e) {
    if (!teamId) throw e;
    await apiRequest<unknown>(`/api/teams/${encodeURIComponent(teamId)}/tasks/${tid}${qs}`, { method: "DELETE" });
    return { ok: true as const };
  }
}

export function updateWebTeamEvent(
  teamId: string,
  eventId: string,
  input: {
    title?: string;
    description?: string | null;
    startDate?: string;
    endDate?: string | null;
    allDay?: boolean;
    color?: string;
    isVideoMeeting?: boolean;
    isHidden?: boolean;
    assigneeIds?: string[];
    reminderMinutes?: number[];
  },
) {
  return apiPatchJson<{ data: ApiCalendarEvent }>(`/web/api/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}`, {
    ...(input.title !== undefined ? { title: input.title } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.startDate !== undefined ? { startDate: input.startDate } : {}),
    ...(input.endDate !== undefined ? { endDate: input.endDate } : {}),
    ...(input.allDay !== undefined ? { allDay: input.allDay } : {}),
    ...(input.color !== undefined ? { color: input.color } : {}),
    ...(input.isVideoMeeting !== undefined ? { isVideoMeeting: input.isVideoMeeting } : {}),
    ...(input.isHidden !== undefined ? { isHidden: input.isHidden } : {}),
    ...(input.assigneeIds !== undefined ? { assigneeIds: input.assigneeIds } : {}),
    ...(input.reminderMinutes !== undefined ? { reminderMinutes: input.reminderMinutes } : {}),
  }).then((r) => r.data);
}

export function approveWebTeamEvent(teamId: string, eventId: string) {
  return apiPostJson<{ data: ApiCalendarEvent }>(
    `/web/api/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}/approve`,
    {},
  ).then((r) => r.data);
}

export function rejectWebTeamEvent(teamId: string, eventId: string) {
  return apiPostJson<{ data: ApiCalendarEvent }>(
    `/web/api/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}/reject`,
    {},
  ).then((r) => r.data);
}

export function fetchTeamActivity(teamId: string) {
  return apiGetJson<{ data: ApiActivityItem[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/activity`,
  ).then((r) => r.data);
}

/** Prefer combined feed; fall back to per-workspace fetch if `/api/activity` is not deployed yet. */
export async function fetchAllActivity(
  workspaces?: { id: string; name: string }[],
): Promise<ApiActivityItem[]> {
  try {
    const data = await apiGetJson<{ data: ApiActivityItem[] }>("/api/activity").then((r) => r.data);
    return Array.isArray(data) ? data : [];
  } catch (e) {
    const msg = e instanceof Error ? e.message : "";
    const missingCombined =
      /not found|404/i.test(msg) || msg.includes("may not exist or you may not have access");
    if (!missingCombined || !workspaces?.length) throw e;

    const chunks = await Promise.all(
      workspaces.map(async (ws) => {
        try {
          const items = await fetchTeamActivity(ws.id);
          return items.map((item) => ({
            ...item,
            teamId: item.teamId ?? ws.id,
            team: item.team ?? { id: ws.id, name: ws.name },
          }));
        } catch {
          return [] as ApiActivityItem[];
        }
      }),
    );

    return chunks
      .flat()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 150);
  }
}

export function postActivityReaction(teamId: string, activityId: string, emoji: string) {
  return apiPostJson<{ data: { toggled: boolean } }>(
    `/api/teams/${encodeURIComponent(teamId)}/activity/${encodeURIComponent(activityId)}/react`,
    { emoji },
  ).then((r) => r.data);
}

export function postActivityCelebrate(
  teamId: string,
  payload: { targetUserId: string; celebrationType: string; message?: string },
) {
  return apiPostJson<{ data: { id: string } }>(`/api/teams/${encodeURIComponent(teamId)}/activity/celebrate`, payload).then((r) => r.data);
}

export type RecognitionRange = "month" | "30d" | "all";

export type RecognitionKpis = {
  recognitionsGivenThisMonth: number;
  recognitionsGivenLastMonth: number;
  recognitionsGivenChangePct: number | null;
  teamMembersRecognizedThisMonth: number;
  totalLast30Days: number;
  totalPrior30Days: number;
  totalChangePct: number | null;
  topRecognizer: {
    userId: string;
    name: string | null;
    image: string | null;
    count: number;
    isCurrentUser: boolean;
  } | null;
};

export type RecognitionItem = {
  id: string;
  createdAt: string;
  celebrationType: string;
  message: string | null;
  giver: { id: string; name: string | null; image: string | null } | null;
  target: { id: string | null; name: string | null; image: string | null };
  reactions: Record<string, ActivityReactionBucket>;
  visibility: "public";
};

export type RecognitionSummary = {
  owner: { id: string; name: string | null; email: string | null; image: string | null } | null;
  kpis: RecognitionKpis;
  breakdown: Array<{ key: string; count: number }>;
  topRecognizers: Array<{
    rank: number;
    userId: string;
    name: string | null;
    image: string | null;
    count: number;
    isCurrentUser: boolean;
  }>;
  items: RecognitionItem[];
  nextCursor: string | null;
  range: string;
  typeFilter: string;
};

export function fetchTeamRecognitions(
  teamId: string,
  opts?: { range?: RecognitionRange; type?: string; limit?: number; cursor?: string | null },
) {
  const params = new URLSearchParams();
  if (opts?.range) params.set("range", opts.range);
  if (opts?.type && opts.type !== "all") params.set("type", opts.type);
  if (opts?.limit) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  return apiGetJson<{ data: RecognitionSummary }>(
    `/api/teams/${encodeURIComponent(teamId)}/recognitions${qs ? `?${qs}` : ""}`,
  ).then((r) => r.data);
}

export function deleteActivityCelebration(teamId: string, activityId: string) {
  return apiDeleteJson<void>(
    `/api/teams/${encodeURIComponent(teamId)}/activity/${encodeURIComponent(activityId)}`,
  );
}

export function fetchUpcomingVideoMeetings() {
  return apiGetJson<{ data: UpcomingVideoMeeting[] }>("/api/video/upcoming").then((r) => r.data);
}

export function createVideoRoom(roomId: string, userName?: string | null) {
  return apiPostJson<{ data: { url: string; token: string } }>("/api/video/room", {
    roomId,
    userName: userName ?? undefined,
  }).then((r) => r.data);
}

export type ApiPollOption = {
  id: string;
  text: string;
  votes: { userId: string }[];
};

export type ApiPoll = {
  id: string;
  teamId: string;
  topicId: string | null;
  question: string;
  endsAt: string;
  createdAt: string;
  createdById: string;
  allowLeaderDelete: boolean;
  createdBy: { id: string; name: string | null; image: string | null };
  options: ApiPollOption[];
  votes: { userId: string; optionId: string }[];
};

/** topicKey `"general"` lists polls for the main team channel (no topic). */
export function fetchTeamPolls(teamId: string, topicKey: string) {
  const qs = topicKey === "general" ? "" : `?topicId=${encodeURIComponent(topicKey)}`;
  return apiGetJson<{ data: ApiPoll[] }>(`/api/teams/${encodeURIComponent(teamId)}/polls${qs}`).then((r) => r.data);
}

export function createTeamPoll(
  teamId: string,
  input: {
    question: string;
    options: string[];
    durationHours: number;
    allowLeaderDelete?: boolean;
    topicId?: string | null;
  },
) {
  return apiPostJson<{ data: ApiPoll }>(`/api/teams/${encodeURIComponent(teamId)}/polls`, {
    question: input.question,
    options: input.options,
    durationHours: input.durationHours,
    allowLeaderDelete: input.allowLeaderDelete ?? true,
    topicId: input.topicId ?? null,
  }).then((r) => r.data);
}

export function voteTeamPoll(teamId: string, pollId: string, optionId: string) {
  return apiPostJson<{ data: ApiPoll }>(
    `/api/teams/${encodeURIComponent(teamId)}/polls/${encodeURIComponent(pollId)}/vote`,
    { optionId },
  ).then((r) => r.data);
}

export type WebTeamMemberRow = {
  id: string;
  userId: string;
  role: string;
  /** When the member joined this workspace (`TeamMember.joinedAt`). */
  joinedAt?: string;
  user: { id: string; name: string | null; email: string | null; image: string | null };
};

export type WebFormerMemberRow = {
  userId: string;
  user: { id: string; name: string | null; email: string; image: string | null };
  isFormer: true;
};

export function fetchFormerWorkspaceMembers(teamId: string) {
  return apiGetJson<{ data: WebFormerMemberRow[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/former-members`,
  ).then((r) => r.data);
}

export type WebTeamDetail = {
  id: string;
  name: string;
  image?: string | null;
  createdAt: string;
  inviteCode?: string | null;
  _count?: { members: number; tasks: number };
  members: WebTeamMemberRow[];
  myRole: string;
  workplaceStandards?: import("./workplace-standards").WorkplaceStandards;
  goFrontendSettings?: import("./go-frontend-settings").GoFrontendSettings;
  requiredCheckInTemplateTitle?: string | null;
};

export type MyJoinRequestRow = {
  id: string;
  teamId: string;
  userId: string;
  status: string;
  createdAt: string;
  team: { id: string; name: string; image: string | null };
};

export function fetchMyJoinRequests() {
  return apiGetJson<{ data: MyJoinRequestRow[] }>("/api/join-requests/mine").then((r) => r.data);
}

export function cancelMyJoinRequest(requestId: string) {
  return apiRequest<unknown>(`/api/join-requests/${encodeURIComponent(requestId)}`, { method: "DELETE" });
}

/** Join-by-code: either a pending approval request or immediate membership (team row). */
export type JoinByCodeResult =
  | { status: "pending"; teamName: string; requestId: string }
  | (WebTeamRow & { role?: string });

export function postJoinTeamByCode(inviteCode: string) {
  return apiPostJson<{ data: JoinByCodeResult }>("/api/teams/join", {
    inviteCode: inviteCode.trim(),
  }).then((r) => r.data);
}

export function createWebTeam(input: { name: string; industry: string; startTrial: true }) {
  return apiPostJson<{ data: WebTeamRow }>("/web/api/teams", {
    name: input.name.trim(),
    industry: input.industry.trim(),
    startTrial: true,
  }).then((r) => r.data);
}

export type WebPaidWorkspaceCheckout = {
  checkoutId: string;
  url: string;
  expiresAt: string;
};

export type WebPaidWorkspaceCheckoutStatus = {
  checkoutId: string;
  status: "pending" | "completed" | "expired" | "failed";
  teamId: string | null;
  team: WebTeamRow | null;
};

export function createWebPaidWorkspaceCheckout(input: {
  name: string;
  industry: string;
  plan: "pro" | "operations";
}) {
  return apiPostJson<{ data: WebPaidWorkspaceCheckout }>("/api/workspace-checkouts", input).then((r) => r.data);
}

export function fetchWebPaidWorkspaceCheckout(checkoutId: string) {
  return apiGetJson<{ data: WebPaidWorkspaceCheckoutStatus }>(
    `/api/workspace-checkouts/${encodeURIComponent(checkoutId)}`,
  ).then((r) => r.data);
}

export function patchApiTeam(teamId: string, body: { name?: string; image?: string | null }) {
  return apiPatchJson<{ data: { id: string; name: string; image?: string | null } }>(
    `/api/teams/${encodeURIComponent(teamId)}`,
    body,
  ).then((r) => r.data);
}

export function patchWebTeamWorkplaceStandards(
  teamId: string,
  workplaceStandards: import("./workplace-standards").WorkplaceStandards,
) {
  return apiPatchJson<{
    data: { workplaceStandards: import("./workplace-standards").WorkplaceStandards };
  }>(`/web/api/teams/${encodeURIComponent(teamId)}`, { workplaceStandards }).then(
    (r) => r.data.workplaceStandards,
  );
}

export function patchWebTeamGoFrontendSettings(
  teamId: string,
  goFrontendSettings: import("./go-frontend-settings").GoFrontendSettings,
) {
  return apiPatchJson<{
    data: { goFrontendSettings: import("./go-frontend-settings").GoFrontendSettings };
  }>(`/web/api/teams/${encodeURIComponent(teamId)}`, { goFrontendSettings }).then(
    (r) => r.data.goFrontendSettings,
  );
}

export type GoLeaderPinStatus = {
  hasPin: boolean;
};

export function fetchGoLeaderPinStatus(teamId: string) {
  return apiGetJson<{ data: GoLeaderPinStatus }>(
    `/api/teams/${encodeURIComponent(teamId)}/members/me/go-pin`,
  ).then((r) => r.data);
}

export function putGoLeaderPin(teamId: string, pin: string) {
  return apiPutJson<{ data: GoLeaderPinStatus }>(
    `/api/teams/${encodeURIComponent(teamId)}/members/me/go-pin`,
    { pin },
  ).then((r) => r.data);
}

export type TeamMemberStatsRow = {
  activeTasks: number;
  overdueTasks: number;
  dueSoonTasks?: number;
  completedTasks: number;
  streak: number;
  personalBestStreak: number;
  activeDevGoals: number;
  devEngagementPct: number;
  daysSinceLastOneOnOne: number | null;
  openFollowUpTasks: number;
  overdueFollowUpTasks: number;
  standardsCompliance?: import("./workplace-standards").MemberStandardsCompliance;
};

export type TeamMemberStatsMap = Record<string, TeamMemberStatsRow>;

export type DevelopmentGoalActivityAlert = {
  goalId: string;
  memberUserId: string;
  memberName: string;
  skill: string;
  daysSinceActivity: number;
  daysUntilInactive: number | null;
};

export type DevelopmentGoalAlerts = {
  nearingInactive: DevelopmentGoalActivityAlert[];
  inactive: DevelopmentGoalActivityAlert[];
};

export type TeamMemberStatsResponse = {
  stats: TeamMemberStatsMap;
  developmentGoalAlerts: DevelopmentGoalAlerts;
  workplaceStandards?: import("./workplace-standards").WorkplaceStandards;
};

export function fetchTeamMemberStats(teamId: string): Promise<TeamMemberStatsResponse> {
  return apiGetJson<{
    data: {
      stats: TeamMemberStatsMap;
      workplaceStandards?: import("./workplace-standards").WorkplaceStandards;
    };
    developmentGoalAlerts?: DevelopmentGoalAlerts;
  }>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks/member-stats`,
  ).then((r) => ({
    stats: r.data.stats,
    developmentGoalAlerts: r.developmentGoalAlerts ?? { nearingInactive: [], inactive: [] },
    workplaceStandards: r.data.workplaceStandards,
  }));
}

export type MonthlyCompletionRow = {
  label: string;
  year: number;
  month: number;
  completionPct: number | null;
  done: number;
  total: number;
};

export function fetchTeamMonthlyCompletion(teamId: string) {
  return apiGetJson<{ data: MonthlyCompletionRow[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks/monthly-completion`,
  ).then((r) => r.data);
}

export function removeTeamMemberApi(teamId: string, userId: string) {
  return apiRequest<unknown>(
    `/api/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export function setTeamMemberRole(teamId: string, userId: string, role: "member" | "team_leader") {
  return apiPatchJson<{ data: unknown }>(
    `/web/api/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/role`,
    { role },
  );
}

export type OwnershipTransferDisposition =
  | "WORKSPACE_ADMIN"
  | "MANAGER"
  | "MEMBER"
  | "REMOVE";

export type OwnershipBillingPath = "KEEP_PAYMENT_METHOD" | "REPLACE_PAYMENT_METHOD";

export type OwnershipTransferRow = {
  id: string;
  teamId: string;
  teamName: string;
  fromUserId: string;
  toUserId: string;
  status: string;
  previousOwnerDisposition: string;
  billingPath: string;
  awaitingPaymentMethod: boolean;
  expiresAt: string;
  acceptedAt: string | null;
  canceledAt: string | null;
  createdAt: string;
  fromUser: { id: string; name: string | null; email: string | null; image: string | null };
  toUser: { id: string; name: string | null; email: string | null; image: string | null };
};

export function initiateOwnershipTransfer(
  teamId: string,
  body: {
    toUserId: string;
    previousOwnerDisposition: OwnershipTransferDisposition;
    billingPath?: OwnershipBillingPath;
    password?: string;
    confirmPhrase?: string;
  },
) {
  return apiPostJson<{ data: OwnershipTransferRow }>(
    `/api/teams/${encodeURIComponent(teamId)}/transfer-ownership`,
    {
      ...body,
      billingPath: body.billingPath ?? "REPLACE_PAYMENT_METHOD",
    },
  );
}

/** @deprecated Use initiateOwnershipTransfer */
export function transferTeamOwnership(teamId: string, userId: string) {
  return initiateOwnershipTransfer(teamId, {
    toUserId: userId,
    previousOwnerDisposition: "WORKSPACE_ADMIN",
    billingPath: "REPLACE_PAYMENT_METHOD",
    confirmPhrase: "TRANSFER",
  });
}

export function fetchPendingOwnershipTransfer(teamId: string) {
  return apiGetJson<{ data: OwnershipTransferRow | null }>(
    `/api/teams/${encodeURIComponent(teamId)}/transfer-ownership/pending`,
  ).then((r) => r.data);
}

export function fetchIncomingOwnershipTransfers() {
  return apiGetJson<{ data: OwnershipTransferRow[] }>("/api/ownership-transfers/mine").then(
    (r) => r.data ?? [],
  );
}

export function acceptOwnershipTransfer(teamId: string, transferId: string) {
  return apiPostJson<{
    data: { transfer: OwnershipTransferRow; paymentSetupUrl: string | null; completed: boolean };
  }>(`/api/teams/${encodeURIComponent(teamId)}/transfer-ownership/${encodeURIComponent(transferId)}/accept`, {});
}

export function completeOwnershipTransferPayment(
  teamId: string,
  transferId: string,
  opts?: { sessionId?: string },
) {
  return apiPostJson<{
    data: {
      transfer: OwnershipTransferRow;
      completed: boolean;
      paymentSetupUrl: string | null;
    };
  }>(
    `/api/teams/${encodeURIComponent(teamId)}/transfer-ownership/${encodeURIComponent(transferId)}/complete-payment`,
    opts?.sessionId ? { sessionId: opts.sessionId } : {},
  );
}

export function declineOwnershipTransfer(teamId: string, transferId: string) {
  return apiPostJson<{ data: OwnershipTransferRow }>(
    `/api/teams/${encodeURIComponent(teamId)}/transfer-ownership/${encodeURIComponent(transferId)}/decline`,
    {},
  );
}

export function cancelOwnershipTransfer(teamId: string, transferId: string) {
  return apiPostJson<{ data: OwnershipTransferRow }>(
    `/api/teams/${encodeURIComponent(teamId)}/transfer-ownership/${encodeURIComponent(transferId)}/cancel`,
    {},
  );
}

export function leaveTeam(teamId: string) {
  return apiRequest<unknown>(`/api/teams/${encodeURIComponent(teamId)}/leave`, { method: "DELETE" });
}

export type DeleteTeamConfirmation = {
  password?: string;
  confirmPhrase?: string;
};

/** Permanently delete a workspace (owner only). Confirm with password or confirmPhrase "DELETE". */
export function deleteTeam(teamId: string, confirmation: DeleteTeamConfirmation) {
  return apiRequest<unknown>(`/api/teams/${encodeURIComponent(teamId)}`, {
    method: "DELETE",
    body: JSON.stringify(confirmation),
  });
}

/** Team photo: same upload endpoint as chat; requires purpose=team and teamId. */
export async function uploadTeamPhoto(file: File, teamId: string): Promise<ChatUploadResult> {
  assertProductionApiConfigured();
  const baseUrl = apiBaseUrl();
  async function doUpload(token: string | null) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", "team");
    formData.append("teamId", teamId);
    const h = new Headers();
    if (token) h.set("Authorization", `Bearer ${token}`);
    try {
      return await fetch(`${baseUrl}/api/upload`, { method: "POST", body: formData, headers: h });
    } catch (e) {
      throw mapNetworkError(e);
    }
  }
  let token = getAccessToken();
  let res = await doUpload(token);
  if (res.status === 401) {
    const recovered = await refreshSessionTokens();
    if (recovered) {
      token = getAccessToken();
      res = await doUpload(token);
    } else {
      clearAccessToken();
    }
  }
  const parsed = (await res.json().catch(() => ({}))) as {
    data?: ChatUploadResult;
    error?: string | { message?: string };
    message?: string;
  };
  if (!res.ok) {
    const msg =
      typeof parsed?.error === "string"
        ? parsed.error
        : typeof parsed?.error === "object" && parsed?.error?.message
          ? parsed.error.message
          : typeof parsed?.message === "string"
            ? parsed.message
            : `Upload failed (${res.status})`;
    throw new Error(msg);
  }
  if (!parsed.data) throw new Error("Upload response missing data.");
  return parsed.data;
}

/** Profile avatar: same upload endpoint as chat; use purpose=profile (no teamId). */
export async function uploadProfilePhoto(file: File): Promise<ChatUploadResult> {
  assertProductionApiConfigured();
  const baseUrl = apiBaseUrl();
  async function doUpload(token: string | null) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("purpose", "profile");
    const h = new Headers();
    if (token) h.set("Authorization", `Bearer ${token}`);
    try {
      return await fetch(`${baseUrl}/api/upload`, { method: "POST", body: formData, headers: h });
    } catch (e) {
      throw mapNetworkError(e);
    }
  }
  let token = getAccessToken();
  let res = await doUpload(token);
  if (res.status === 401) {
    const recovered = await refreshSessionTokens();
    if (recovered) {
      token = getAccessToken();
      res = await doUpload(token);
    } else {
      clearAccessToken();
    }
  }
  const parsed = (await res.json().catch(() => ({}))) as {
    data?: ChatUploadResult;
    error?: string | { message?: string };
    message?: string;
  };
  if (!res.ok) {
    const msg =
      typeof parsed?.error === "string"
        ? parsed.error
        : typeof parsed?.error === "object" && parsed?.error?.message
          ? parsed.error.message
          : typeof parsed?.message === "string"
            ? parsed.message
            : `Upload failed (${res.status})`;
    throw new Error(msg);
  }
  if (!parsed.data) throw new Error("Upload response missing data.");
  return parsed.data;
}

export async function fetchWebTeam(teamId: string) {
  const [webRes, mobileRes] = await Promise.all([
    apiGetJson<{ data: WebTeamDetail }>(`/web/api/teams/${encodeURIComponent(teamId)}`),
    apiGetJson<{ data: Array<{ id: string; image?: string | null }> }>("/api/teams").catch(() => null),
  ]);
  const mobileImage = mobileRes?.data?.find((t) => t.id === teamId)?.image;
  const team = webRes.data;
  return {
    ...team,
    image: team.image || mobileImage || null,
  };
}

export type CreateWebTaskInput = {
  teamId: string;
  title: string;
  description?: string | null;
  priority?: string;
  status?: string;
  kind: "workspace_task" | "reminder";
  dueDate?: string | null;
  assigneeIds: string[];
  isJoint?: boolean;
  incognito?: boolean;
  subtasks?: string[];
  attachmentUrl?: string | null;
  recurrence?: {
    type: string;
    occurrenceCount?: number;
    /** @deprecated Use occurrenceCount */
    interval?: number;
    daysOfWeek?: string | null;
    dayOfMonth?: number | null;
  };
  timeZone?: string;
};

export function createTeamTask(input: CreateWebTaskInput) {
  const teamId = encodeURIComponent(input.teamId);
  return apiPostJson<{ data: ApiTask[] | { tasks?: ApiTask[] } }>(
    `/api/teams/${teamId}/tasks`,
    {
      title: input.title,
      description: input.description || undefined,
      priority: input.priority ?? "medium",
      status: input.status ?? "todo",
      kind: input.kind,
      dueDate: input.dueDate ? calendarDuePayload(input.dueDate, input.timeZone) : undefined,
      assigneeIds: input.assigneeIds,
      isJoint: input.isJoint === true,
      incognito: input.incognito === true,
      attachmentUrl: input.attachmentUrl || undefined,
      recurrence: input.recurrence,
      timeZone: input.timeZone,
      subtasks: input.subtasks?.filter((t) => t.trim()).map((title) => ({ title: title.trim() })),
    },
  ).then((r) => {
    const data = r.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object" && Array.isArray((data as { tasks?: ApiTask[] }).tasks)) {
      return (data as { tasks: ApiTask[] }).tasks;
    }
    return [];
  });
}

export function createWebTask(input: CreateWebTaskInput) {
  const hasSubtasks = (input.subtasks?.filter((t) => t.trim()).length ?? 0) > 0;
  if (input.recurrence || input.attachmentUrl || hasSubtasks) {
    return createTeamTask(input);
  }
  return apiPostJson<{ data: { tasks: ApiTask[] } }>("/web/api/tasks", {
    teamId: input.teamId,
    title: input.title,
    description: input.description || undefined,
    priority: input.priority ?? "medium",
    status: input.status ?? "todo",
    kind: input.kind,
    dueDate: input.dueDate ? calendarDuePayload(input.dueDate, input.timeZone) : undefined,
    timeZone: input.timeZone,
    assigneeIds: input.assigneeIds,
    isJoint: input.isJoint === true,
    incognito: input.incognito === true,
    subtasks: input.subtasks?.filter((t) => t.trim()).length ? input.subtasks.filter((t) => t.trim()) : undefined,
  }).then((r) => r.data.tasks);
}

export function assignTeamTaskMembers(teamId: string, taskId: string, userIds: string[]) {
  return apiPostJson<{ data: ApiTask }>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(taskId)}/assign`,
    { userIds },
  ).then((r) => r.data);
}

export function unassignTeamTaskMember(teamId: string, taskId: string, userId: string) {
  return apiRequest<unknown>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(taskId)}/assign/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

export function createTeamTaskSubtask(teamId: string, taskId: string, title: string) {
  return apiPostJson<{ data: ApiSubtask }>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(taskId)}/subtasks`,
    { title },
  ).then((r) => r.data);
}

export function updateTeamTaskSubtask(
  teamId: string,
  taskId: string,
  subtaskId: string,
  completed: boolean,
) {
  return apiPatchJson<{ data: ApiSubtask }>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(taskId)}/subtasks/${encodeURIComponent(subtaskId)}`,
    { completed },
  ).then((r) => r.data);
}

export function deleteTeamTaskSubtask(teamId: string, taskId: string, subtaskId: string) {
  return apiRequest<unknown>(
    `/api/teams/${encodeURIComponent(teamId)}/tasks/${encodeURIComponent(taskId)}/subtasks/${encodeURIComponent(subtaskId)}`,
    { method: "DELETE" },
  );
}

export function fetchTaskTemplates(teamId: string) {
  return apiGetJson<{ data: TaskTemplate[] }>(
    `/api/teams/${encodeURIComponent(teamId)}/templates`,
  ).then((r) => r.data);
}

export function saveTaskTemplate(
  teamId: string,
  input: {
    title: string;
    description?: string | null;
    priority?: string;
    attachmentUrl?: string | null;
    subtasks?: Array<{ title: string; order: number }>;
    isRecurring?: boolean;
    recurrenceType?: string | null;
    recurrenceInterval?: number | null;
    recurrenceDaysOfWeek?: string | null;
    recurrenceDayOfMonth?: number | null;
    incognito?: boolean;
    isJoint?: boolean;
  },
) {
  return apiPostJson<{ data: TaskTemplate }>(
    `/api/teams/${encodeURIComponent(teamId)}/templates`,
    input,
  ).then((r) => r.data);
}

export function deleteTaskTemplate(teamId: string, templateId: string) {
  return apiRequest<unknown>(
    `/api/teams/${encodeURIComponent(teamId)}/templates/${encodeURIComponent(templateId)}`,
    { method: "DELETE" },
  );
}

export type OneOnOneTemplateFieldType =
  | "section"
  | "short_text"
  | "long_text"
  | "rating"
  | "yes_no"
  | "manager_notes"
  | "associate_notes";

export type AssociateRequestMode = "task" | "message";

export type OneOnOneTemplateField = {
  id: string;
  label: string;
  type: OneOnOneTemplateFieldType;
  order: number;
  required?: boolean;
  ratingMax?: number;
  helpText?: string | null;
  associateRequest?: AssociateRequestMode | null;
};

export type OneOnOneAssociateFeedbackContext = {
  fieldId: string;
  fieldLabel: string;
  helpText: string | null;
  meetingTitle: string;
  currentResponse: string;
  submitted: boolean;
  associateRequest: AssociateRequestMode | null;
  leaderComments: string | null;
  leaderCommentsLabel: string | null;
  leaderCommentsFrom: string | null;
};

export type OneOnOneTemplate = {
  id: string;
  teamId: string;
  title: string;
  description: string | null;
  libraryKey?: string | null;
  fields: OneOnOneTemplateField[];
  leaderPrep?: string[];
  createdById: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; name: string; email: string; image: string | null };
};

export type CheckInLibraryTemplate = {
  key: string;
  title: string;
  description: string | null;
  fields: OneOnOneTemplateField[];
};

export type OneOnOneTemplateInput = {
  title: string;
  description?: string | null;
  fields: OneOnOneTemplateField[];
  leaderPrep?: string[];
};

function requireTeamId(teamId: string): string {
  const id = teamId?.trim();
  if (!id) throw new Error("No workspace selected.");
  return id;
}

function oneOnOneTemplatesPaths(teamId: string) {
  const id = encodeURIComponent(requireTeamId(teamId));
  return {
    api: `/api/teams/${id}/one-on-one-templates`,
    web: `/web/api/teams/${id}/one-on-one-templates`,
  };
}

function oneOnOneMeetingsPaths(teamId: string, memberUserId: string) {
  const team = encodeURIComponent(requireTeamId(teamId));
  const member = encodeURIComponent(requireTeamId(memberUserId));
  return {
    api: `/api/teams/${team}/members/${member}/one-on-ones`,
    web: `/web/api/teams/${team}/members/${member}/one-on-ones`,
  };
}

async function oneOnOneRequest<T>(paths: { api: string; web: string }, init?: RequestInit): Promise<T> {
  try {
    return await apiRequest<T>(paths.api, init);
  } catch (e) {
    const msg = e instanceof Error ? e.message : "";
    if (msg.includes("Not found")) {
      return apiRequest<T>(paths.web, init);
    }
    throw e;
  }
}

export function fetchOneOnOneTemplates(teamId: string) {
  const paths = oneOnOneTemplatesPaths(teamId);
  return oneOnOneRequest<{ data: OneOnOneTemplate[] }>(paths).then((r) => r.data ?? []);
}

export function createOneOnOneTemplate(teamId: string, input: OneOnOneTemplateInput) {
  const paths = oneOnOneTemplatesPaths(teamId);
  return oneOnOneRequest<{ data: OneOnOneTemplate }>(paths, {
    method: "POST",
    body: JSON.stringify(input),
  }).then((r) => r.data);
}

export function updateOneOnOneTemplate(teamId: string, templateId: string, input: OneOnOneTemplateInput) {
  const paths = oneOnOneTemplatesPaths(teamId);
  const suffix = `/${encodeURIComponent(templateId)}`;
  return oneOnOneRequest<{ data: OneOnOneTemplate }>(
    { api: `${paths.api}${suffix}`, web: `${paths.web}${suffix}` },
    { method: "PATCH", body: JSON.stringify(input) },
  ).then((r) => r.data);
}

export function deleteOneOnOneTemplate(teamId: string, templateId: string) {
  const paths = oneOnOneTemplatesPaths(teamId);
  const suffix = `/${encodeURIComponent(templateId)}`;
  return oneOnOneRequest<{ data: { deleted: boolean } }>(
    { api: `${paths.api}${suffix}`, web: `${paths.web}${suffix}` },
    { method: "DELETE" },
  );
}

export function fetchCheckInTemplateLibrary() {
  return oneOnOneRequest<{ data: CheckInLibraryTemplate[] }>({
    api: "/api/check-in-template-library",
    web: "/web/api/check-in-template-library",
  }).then((r) => r.data ?? []);
}

export function addCheckInTemplateFromLibrary(teamId: string, libraryKey: string) {
  const paths = oneOnOneTemplatesPaths(teamId);
  const suffix = `/from-library/${encodeURIComponent(libraryKey)}`;
  return oneOnOneRequest<{ data: OneOnOneTemplate }>(
    { api: `${paths.api}${suffix}`, web: `${paths.web}${suffix}` },
    { method: "POST" },
  ).then((r) => r.data);
}

export type OneOnOneFollowUpTask = {
  id: string;
  title: string;
  description: string | null;
  status: string;
  dueDate: string | null;
  assignee: { id: string; name: string | null; email: string; image: string | null } | null;
};

export type OneOnOneFollowUpTaskInput = {
  title: string;
  assigneeUserId: string;
  description?: string;
  dueDate?: string;
};

export type OneOnOneMeeting = {
  id: string;
  teamId: string;
  memberUserId: string;
  templateId: string | null;
  templateTitle: string;
  templateFields: OneOnOneTemplateField[];
  responses: Record<string, string | number>;
  status?: "draft" | "published";
  publishedAt?: string | null;
  createdById: string;
  createdAt: string;
  createdBy?: { id: string; name: string; email: string; image: string | null };
  followUpTasks?: OneOnOneFollowUpTask[];
  associateFeedbackPending?: boolean;
};

export function fetchOneOnOneMeetings(teamId: string, memberUserId: string) {
  const paths = oneOnOneMeetingsPaths(teamId, memberUserId);
  return oneOnOneRequest<{ data: OneOnOneMeeting[] }>(paths).then((r) => r.data ?? []);
}

export function createOneOnOneMeeting(
  teamId: string,
  memberUserId: string,
  input: {
    templateId: string;
    responses: Record<string, string | number>;
    followUpTasks?: OneOnOneFollowUpTaskInput[];
    requestAssociateFeedback?: boolean;
    status?: "draft" | "published";
  },
) {
  const paths = oneOnOneMeetingsPaths(teamId, memberUserId);
  return oneOnOneRequest<{ data: OneOnOneMeeting }>(paths, {
    method: "POST",
    body: JSON.stringify(input),
  }).then((r) => r.data);
}

function oneOnOneMeetingPaths(teamId: string, memberUserId: string, meetingId: string) {
  const team = encodeURIComponent(requireTeamId(teamId));
  const member = encodeURIComponent(requireTeamId(memberUserId));
  const meeting = encodeURIComponent(meetingId);
  const suffix = `/one-on-ones/${meeting}`;
  return {
    api: `/api/teams/${team}/members/${member}${suffix}`,
    web: `/web/api/teams/${team}/members/${member}${suffix}`,
  };
}

export function updateOneOnOneMeeting(
  teamId: string,
  memberUserId: string,
  meetingId: string,
  input: {
    responses: Record<string, string | number>;
    followUpTasks?: OneOnOneFollowUpTaskInput[];
    requestAssociateFeedback?: boolean;
    status?: "draft" | "published";
  },
) {
  const paths = oneOnOneMeetingPaths(teamId, memberUserId, meetingId);
  return oneOnOneRequest<{ data: OneOnOneMeeting }>(paths, {
    method: "PATCH",
    body: JSON.stringify(input),
  }).then((r) => r.data);
}

export function deleteOneOnOneMeeting(teamId: string, memberUserId: string, meetingId: string) {
  const paths = oneOnOneMeetingPaths(teamId, memberUserId, meetingId);
  return oneOnOneRequest<{ data: { deleted: boolean } }>(paths, { method: "DELETE" });
}

function oneOnOneAssociateFeedbackPaths(teamId: string, memberUserId: string, meetingId: string, fieldId?: string) {
  const base = oneOnOneMeetingPaths(teamId, memberUserId, meetingId);
  const suffix = fieldId ? `/associate-feedback/${encodeURIComponent(fieldId)}` : "/associate-feedback";
  return {
    api: `${base.api}${suffix}`,
    web: `${base.web}${suffix}`,
  };
}

export function fetchOneOnOneAssociateFeedbackContext(
  teamId: string,
  memberUserId: string,
  meetingId: string,
  fieldId: string,
) {
  const paths = oneOnOneAssociateFeedbackPaths(teamId, memberUserId, meetingId, fieldId);
  return oneOnOneRequest<{ data: OneOnOneAssociateFeedbackContext }>(paths).then((r) => r.data);
}

export function submitOneOnOneAssociateFeedback(
  teamId: string,
  memberUserId: string,
  meetingId: string,
  input: { fieldId: string; response: string },
) {
  const paths = oneOnOneAssociateFeedbackPaths(teamId, memberUserId, meetingId);
  return oneOnOneRequest<{ data: OneOnOneMeeting }>(paths, {
    method: "POST",
    body: JSON.stringify(input),
  }).then((r) => r.data);
}

export type DevelopmentGoalNote = {
  id: string;
  body: string;
  createdAt: string;
  createdById: string;
  createdBy: { id: string; name: string; email: string; image: string | null };
};

export type DevelopmentGoalStatus = "active" | "inactive" | "closed";

export type DevelopmentGoal = {
  id: string;
  teamId: string;
  memberUserId: string;
  skill: string;
  description: string | null;
  dueDate: string | null;
  priority: "low" | "normal" | "high";
  steps: string[];
  completedStepIndexes: number[];
  completedStepDates: Record<number, string>;
  completedStepCount: number;
  totalStepCount: number;
  progressPercent: number;
  status: DevelopmentGoalStatus;
  closedAt: string | null;
  lastActivityAt: string;
  daysSinceActivity: number;
  daysUntilInactive: number | null;
  nearingInactive: boolean;
  inactivityPolicyDays: number;
  createdById: string;
  createdAt: string;
  createdBy?: { id: string; name: string; email: string; image: string | null };
  notes: DevelopmentGoalNote[];
};

function developmentGoalsPaths(teamId: string, memberUserId: string, goalId?: string) {
  const team = encodeURIComponent(requireTeamId(teamId));
  const member = encodeURIComponent(requireTeamId(memberUserId));
  const base = `/api/teams/${team}/members/${member}/development-goals`;
  const webBase = `/web/api/teams/${team}/members/${member}/development-goals`;
  if (!goalId) return { api: base, web: webBase };
  const suffix = `/${encodeURIComponent(goalId)}`;
  return { api: `${base}${suffix}`, web: `${webBase}${suffix}` };
}

function isLikelyMissingRouteError(message: string): boolean {
  return (
    message === "Not found" ||
    message === "Not found — this may not exist or you may not have access."
  );
}

async function developmentGoalsRequest<T>(paths: { api: string; web: string }, init?: RequestInit): Promise<T> {
  try {
    return await apiRequest<T>(paths.api, init);
  } catch (e) {
    const msg = e instanceof Error ? e.message : "";
    if (isLikelyMissingRouteError(msg)) {
      return apiRequest<T>(paths.web, init);
    }
    throw e;
  }
}

export function fetchDevelopmentGoals(teamId: string, memberUserId: string) {
  const paths = developmentGoalsPaths(teamId, memberUserId);
  return developmentGoalsRequest<{ data: DevelopmentGoal[] }>(paths).then((r) => r.data ?? []);
}

export function createDevelopmentGoal(
  teamId: string,
  memberUserId: string,
  input: { skill: string; steps: string[] },
) {
  const paths = developmentGoalsPaths(teamId, memberUserId);
  return developmentGoalsRequest<{ data: DevelopmentGoal }>(paths, {
    method: "POST",
    body: JSON.stringify(input),
  }).then((r) => r.data);
}

export function updateDevelopmentGoal(
  teamId: string,
  memberUserId: string,
  goalId: string,
  input: { skill: string; steps: string[] },
) {
  const paths = developmentGoalsPaths(teamId, memberUserId, goalId);
  return developmentGoalsRequest<{ data: DevelopmentGoal }>(paths, {
    method: "PATCH",
    body: JSON.stringify(input),
  }).then((r) => r.data);
}

export function addDevelopmentGoalNote(
  teamId: string,
  memberUserId: string,
  goalId: string,
  body: string,
) {
  const paths = developmentGoalsPaths(teamId, memberUserId, goalId);
  return developmentGoalsRequest<{ data: DevelopmentGoal }>(
    { api: `${paths.api}/notes`, web: `${paths.web}/notes` },
    { method: "POST", body: JSON.stringify({ body }) },
  ).then((r) => r.data);
}

export function updateDevelopmentGoalNote(
  teamId: string,
  memberUserId: string,
  goalId: string,
  noteId: string,
  body: string,
) {
  const paths = developmentGoalsPaths(teamId, memberUserId, goalId);
  const note = encodeURIComponent(noteId);
  return developmentGoalsRequest<{ data: DevelopmentGoal }>(
    { api: `${paths.api}/notes/${note}`, web: `${paths.web}/notes/${note}` },
    { method: "PATCH", body: JSON.stringify({ body }) },
  ).then((r) => r.data);
}

export function deleteDevelopmentGoalNote(
  teamId: string,
  memberUserId: string,
  goalId: string,
  noteId: string,
) {
  const paths = developmentGoalsPaths(teamId, memberUserId, goalId);
  const note = encodeURIComponent(noteId);
  return developmentGoalsRequest<{ data: DevelopmentGoal }>(
    { api: `${paths.api}/notes/${note}`, web: `${paths.web}/notes/${note}` },
    { method: "DELETE" },
  ).then((r) => r.data);
}

export function setDevelopmentGoalStatus(
  teamId: string,
  memberUserId: string,
  goalId: string,
  status: DevelopmentGoalStatus,
) {
  const paths = developmentGoalsPaths(teamId, memberUserId, goalId);
  return developmentGoalsRequest<{ data: DevelopmentGoal }>(
    { api: `${paths.api}/status`, web: `${paths.web}/status` },
    { method: "PATCH", body: JSON.stringify({ status }) },
  ).then((r) => r.data);
}

export function deleteDevelopmentGoal(teamId: string, memberUserId: string, goalId: string) {
  const paths = developmentGoalsPaths(teamId, memberUserId, goalId);
  return developmentGoalsRequest<{ data: { deleted: boolean } }>(paths, {
    method: "DELETE",
  });
}

export type PublicChecklistHubPayload = {
  team: { name: string; image: string | null };
  checklists: [];
  alertSoundUrl?: string;
  quickActions?: import("./go-frontend-settings").GoFrontendQuickAction[];
};

export function fetchPublicChecklistHub(hubToken: string, deviceId?: string) {
  const qs = deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : "";
  return apiRequest<{ data: PublicChecklistHubPayload }>(
    `/api/public/checklist-hubs/${encodeURIComponent(hubToken)}${qs}`,
    { method: "GET", cache: "no-store" },
  ).then((r) => r.data);
}

export function workspaceChecklistHubUrl(hubToken: string): string {
  if (typeof window !== "undefined") {
    return `${window.location.origin}/checklist/${hubToken}`;
  }
  return `/checklist/${hubToken}`;
}

/** @deprecated Use workspaceChecklistHubUrl */
export function checklistPublicUrl(publicToken: string): string {
  return workspaceChecklistHubUrl(publicToken);
}

export type OrganizationSsoPublicConfig = {
  provider: string;
  protocol: string;
  enabled: boolean;
  issuer: string | null;
  clientId: string | null;
  hasClientSecret: boolean;
  domain: string | null;
  domainVerified: boolean;
  ssoRequired: boolean;
  organizationId: string;
  organizationName: string;
  callbackUrl: string;
};

export type OrganizationScimPublicConfig = {
  enabled: boolean;
  hasToken: boolean;
  tokenPrefix: string | null;
  baseUrl: string;
  organizationId: string;
  token?: string;
};

export type OrganizationForTeamResponse = {
  team: { id: string; name: string; organizationId: string | null };
  organization: { id: string; name: string; slug: string; ssoRequired: boolean } | null;
  sso: OrganizationSsoPublicConfig | { enabled: boolean; provider: string; organizationName: string } | null;
  scim: OrganizationScimPublicConfig | { enabled: boolean } | null;
};

export function fetchOrganizationForTeam(teamId: string) {
  return apiGetJson<{ data: OrganizationForTeamResponse }>(
    `/api/organizations/for-team/${encodeURIComponent(teamId)}`,
  ).then((r) => r.data);
}

export function createOrganizationFromTeam(teamId: string, name?: string) {
  return apiPostJson<{
    data: {
      organization: { id: string; name: string; slug: string } | null;
      sso: OrganizationSsoPublicConfig | null;
    };
  }>(`/api/organizations/from-team/${encodeURIComponent(teamId)}`, name ? { name } : {}).then((r) => r.data);
}

export function saveOrganizationOktaSso(
  organizationId: string,
  body: {
    issuer: string;
    clientId: string;
    clientSecret?: string;
    domain: string;
    enabled?: boolean;
    ssoRequired?: boolean;
    markDomainVerified?: boolean;
  },
) {
  return apiPutJson<{ data: OrganizationSsoPublicConfig }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/sso/okta`,
    body,
  ).then((r) => r.data);
}

export function discoverSsoForEmail(email: string) {
  return apiPostJson<{
    data: {
      ssoAvailable: boolean;
      provider?: string;
      organizationId?: string;
      organizationName?: string;
      domain?: string;
      ssoRequired?: boolean;
      startPath?: string;
      reason?: string;
    };
  }>("/api/sso/discover", { email }).then((r) => r.data);
}

export function fetchOrganizationScim(organizationId: string) {
  return apiGetJson<{ data: OrganizationScimPublicConfig }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/scim`,
  ).then((r) => r.data);
}

export function saveOrganizationScim(organizationId: string, enabled: boolean) {
  return apiPutJson<{ data: OrganizationScimPublicConfig }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/scim`,
    { enabled },
  ).then((r) => r.data);
}

export function regenerateOrganizationScimToken(organizationId: string) {
  return apiPostJson<{ data: OrganizationScimPublicConfig }>(
    `/api/organizations/${encodeURIComponent(organizationId)}/scim/token`,
    {},
  ).then((r) => r.data);
}
